import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Button, Callout, Card, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, Title } from '@tremor/react';
import { currentUser } from '../api/auth';
import { fetchOverview } from '../api/client';
import { fetchDashboard, saveDashboard, resetDashboard } from '../api/dashboard';
import { runReport, type ReportRunOverrides } from '../api/reports';
import { canSeeMargin } from '../lib/roles';
import { sliceOverviewByStore } from '../lib/overviewStoreSlice';
import { t } from '../lib/i18n';
import { formatInt } from '../lib/format';
import Header from '../components/Header';
import ExceptionCards from '../components/ExceptionCards';
import KpiRow from '../components/KpiRow';
import SalesPeriodChart from '../components/SalesPeriodChart';
import SalesByStoreChart from '../components/SalesByStoreChart';
import TopPartsTable from '../components/TopPartsTable';
import TopCustomersTable from '../components/TopCustomersTable';
import LowStockList from '../components/LowStockList';
import SlowMoversList from '../components/SlowMoversList';
import FreshnessFooter from '../components/FreshnessFooter';
import WidgetFrame from '../components/WidgetFrame';
import DashboardWidgetPicker from '../components/DashboardWidgetPicker';
import ConfirmDialog from '../components/ConfirmDialog';
import type { DashboardLayout, DashboardWidget, OverviewPayload, ReportRunResult, User } from '../api/types';

// The Resumen default, used when the user has saved no layout of their own.
const DEFAULT_LAYOUT: DashboardWidget[] = [
  { id: 'd-kpis', kind: 'chart', source: 'kpis', w: 2 },
  { id: 'd-exceptions', kind: 'chart', source: 'exceptions', w: 2 },
  { id: 'd-sales-period', kind: 'chart', source: 'sales-period', w: 1 },
  { id: 'd-sales-by-store', kind: 'chart', source: 'sales-by-store', w: 1 },
  { id: 'd-top-parts', kind: 'chart', source: 'top-parts', w: 1 },
  { id: 'd-top-customers', kind: 'chart', source: 'top-customers', w: 1 },
  { id: 'd-low-stock', kind: 'chart', source: 'low-stock', w: 2 },
  { id: 'd-slow-movers', kind: 'chart', source: 'slow-movers', w: 2 },
];

export default function Tablero() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    currentUser().then((u) => { if (alive) setUser(u); }).catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);
  if (user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <TableroInner user={user} />;
}

function TableroInner({ user }: { user: User }) {
  const [data, setData] = useState<OverviewPayload | null>(null);
  const [layout, setLayout] = useState<DashboardLayout | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<DashboardWidget[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [confirmingReset, setConfirmingReset] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([fetchOverview(user.role), fetchDashboard()])
      .then(([ov, dl]) => { if (alive) { setData(ov); setLayout(dl); } })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [user.role]);

  const widgets = layout && layout.widgets.length > 0 ? layout.widgets : DEFAULT_LAYOUT;
  const shown = editing ? draft : widgets;

  function startEditing() {
    setDraft(widgets);
    setSaveErr(null);
    setEditing(true);
  }
  function cancelEditing() {
    setEditing(false);
    setConfirmingReset(false);
  }
  function moveWidget(idx: number, delta: number) {
    setDraft((cur) => {
      const next = cur.slice();
      const target = idx + delta;
      if (target < 0 || target >= next.length) return cur;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  }
  function removeWidget(idx: number) {
    setDraft((cur) => cur.filter((_, i) => i !== idx));
  }
  function updateWidget(idx: number, nextWidget: DashboardWidget) {
    setDraft((cur) => cur.map((w, i) => (i === idx ? nextWidget : w)));
  }
  function addWidget(w: DashboardWidget) {
    setDraft((cur) => [...cur, w]);
  }

  async function handleSave() {
    setSaving(true);
    setSaveErr(null);
    try {
      const saved = await saveDashboard({ version: layout?.version ?? 1, widgets: draft });
      setLayout(saved);
      setEditing(false);
    } catch (e) {
      setSaveErr((e as Error).message || t.tablero.edit.saveError);
    } finally {
      setSaving(false);
    }
  }

  async function handleReset() {
    setSaving(true);
    setSaveErr(null);
    try {
      await resetDashboard();
      setLayout({ version: layout?.version ?? 1, widgets: [] });
      setEditing(false);
      setConfirmingReset(false);
    } catch (e) {
      setSaveErr((e as Error).message || t.tablero.edit.saveError);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} />
      <main className="flex-1 px-6 py-6 space-y-4 max-w-screen-2xl mx-auto w-full">
        <div className="flex items-baseline gap-3 flex-wrap">
          <Title>{t.tablero.title}</Title>
          <Text className="text-tremor-content text-sm">{t.tablero.subtitle}</Text>
          <div className="ml-auto flex items-center gap-2">
            {!editing ? (
              <Button size="xs" variant="secondary" onClick={startEditing} disabled={!data}>
                {t.tablero.edit.editButton}
              </Button>
            ) : (
              <>
                <Button size="xs" variant="light" onClick={() => setConfirmingReset(true)} disabled={saving}>
                  {t.tablero.edit.resetButton}
                </Button>
                <Button size="xs" variant="secondary" onClick={cancelEditing} disabled={saving}>
                  {t.tablero.edit.cancelButton}
                </Button>
                <Button size="xs" variant="primary" onClick={handleSave} loading={saving}>
                  {t.tablero.edit.saveButton}
                </Button>
              </>
            )}
          </div>
        </div>

        {error && <Card><Text className="text-red-600 dark:text-red-400">{error}</Text></Card>}
        {saveErr && <Callout title={t.tablero.edit.saveError} color="rose">{saveErr}</Callout>}
        {!data && !error && <Text className="text-tremor-content">{t.tablero.loading}</Text>}

        {data && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {shown.map((wd, idx) => (
              <div key={wd.id} className={wd.w === 2 ? 'lg:col-span-2' : ''}>
                <WidgetFrame
                  widget={wd}
                  editing={editing}
                  isFirst={idx === 0}
                  isLast={idx === shown.length - 1}
                  stores={data.stores}
                  onMove={(delta) => moveWidget(idx, delta)}
                  onRemove={() => removeWidget(idx)}
                  onChange={(next) => updateWidget(idx, next)}
                >
                  {wd.kind === 'report'
                    ? <ReportWidget id={wd.source} title={wd.title} overrides={filtersToOverrides(wd.filters)} />
                    : renderChart(wd.source, data, user, filterSid(wd.filters))}
                </WidgetFrame>
              </div>
            ))}
          </div>
        )}

        {editing && <DashboardWidgetPicker currentWidgets={draft} onAdd={addWidget} />}

        {confirmingReset && (
          <ConfirmDialog
            title={t.tablero.edit.resetButton}
            message={t.tablero.edit.resetConfirm}
            confirmLabel={t.tablero.edit.resetButton}
            cancelLabel={t.tablero.edit.cancelButton}
            onConfirm={handleReset}
            onCancel={() => setConfirmingReset(false)}
          />
        )}
      </main>
      <FreshnessFooter meta={data?.meta ?? null} />
    </div>
  );
}

function filterSid(filters: DashboardWidget['filters']): number | null {
  const v = filters?.sid;
  return typeof v === 'number' ? v : null;
}

function filtersToOverrides(filters: DashboardWidget['filters']): ReportRunOverrides | undefined {
  if (!filters) return undefined;
  const overrides: ReportRunOverrides = {};
  if (typeof filters.from === 'string') overrides.from = filters.from;
  if (typeof filters.to === 'string') overrides.to = filters.to;
  return Object.keys(overrides).length > 0 ? overrides : undefined;
}

function renderChart(source: string, fullData: OverviewPayload, user: User, sid: number | null) {
  const data = sliceOverviewByStore(fullData, sid);
  switch (source) {
    case 'kpis':
      return <KpiRow kpis={data.kpis} showMargin={canSeeMargin(user)} />;
    case 'exceptions':
      return <ExceptionCards />;
    case 'sales-period':
      return <SalesPeriodChart today={data.sales_today} sid={sid} onSelectHour={() => {}} />;
    case 'sales-by-store':
      return data.sales_by_store.length > 0
        ? <SalesByStoreChart rows={data.sales_by_store} onSelectStore={() => {}} />
        : <Card><Text className="text-tremor-content text-sm">{t.tablero.singleStore}</Text></Card>;
    case 'top-parts':
      return <TopPartsTable rows={data.top_parts} />;
    case 'top-customers':
      return <TopCustomersTable rows={data.top_customers} />;
    case 'low-stock':
      return <LowStockList rows={data.low_stock} stores={data.stores} />;
    case 'slow-movers':
      return <SlowMoversList rows={data.slow_movers} stores={data.stores} />;
    default:
      return <Card><Text className="text-tremor-content text-sm">{t.tablero.widgetUnavailable}</Text></Card>;
  }
}

// ReportWidget runs a saved report and shows a compact preview (first rows). A
// report the user can no longer see returns an error → an inline notice.
// `overrides` (JCD-17 phase 3) re-runs the report when the widget's own
// date-range filter changes — keyed on its JSON form since a fresh object
// literal is passed every render.
function ReportWidget({ id, title, overrides }: { id: string; title?: string; overrides?: ReportRunOverrides }) {
  const [result, setResult] = useState<ReportRunResult | null>(null);
  const [err, setErr] = useState(false);
  const overridesKey = JSON.stringify(overrides ?? {});
  useEffect(() => {
    let alive = true;
    setResult(null);
    setErr(false);
    runReport(id, overrides).then((r) => { if (alive) setResult(r); }).catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, overridesKey]);

  if (err) {
    return (
      <Card>
        <Title className="text-base">{title || t.tablero.reportWidget}</Title>
        <Text className="text-tremor-content text-sm mt-1">{t.tablero.reportUnavailable}</Text>
      </Card>
    );
  }
  if (!result) {
    return (
      <Card>
        <Title className="text-base">{title || t.tablero.reportWidget}</Title>
        <Text className="text-tremor-content text-sm mt-1">{t.tablero.loading}</Text>
      </Card>
    );
  }
  const cols = result.rows.length > 0 ? Object.keys(result.rows[0]) : [];
  const rows = result.rows.slice(0, 8);
  return (
    <Card>
      <Title className="text-base">{title || t.tablero.reportWidget}</Title>
      {cols.length === 0 ? (
        <Text className="text-tremor-content text-sm mt-1">{t.tablero.reportEmpty}</Text>
      ) : (
        <div className="overflow-x-auto">
          <Table className="mt-2">
            <TableHead>
              <TableRow>{cols.map((c) => <TableHeaderCell key={c}>{c}</TableHeaderCell>)}</TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, i) => (
                <TableRow key={i}>
                  {cols.map((c) => <TableCell key={c}>{fmtCell(row[c])}</TableCell>)}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <Text className="text-tremor-content text-xs mt-2">{formatInt(result.row_count)} {t.tablero.rows}</Text>
    </Card>
  );
}

function fmtCell(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number') return v.toLocaleString('es-MX');
  return String(v);
}
